import { CATEGORIES } from '../constants';
import { Career, Category } from '../types';

// Simple in-memory cache to prevent redundant network requests
const careerCache: Record<string, Career> = {};
let careerIndexCache: any[] | null = null;

const fetchIndex = async () => {
  if (careerIndexCache) return careerIndexCache;
  try {
    // Explicitly fetching from the root to ensure reliability in nested routes
    const response = await fetch('/data/careers/index.json');
    if (!response.ok) throw new Error('Failed to load career index');
    careerIndexCache = await response.json();
    return careerIndexCache;
  } catch (error) {
    console.error('Error loading career index:', error);
    return [];
  }
};

export const getAllCareers = async (): Promise<any[]> => {
  return await fetchIndex();
};

export const getFeaturedCareers = async (): Promise<any[]> => {
  const index = await fetchIndex();
  return index.filter((c: any) => c.featured);
};

export const getCareerBySlug = async (slug: string): Promise<Career | undefined> => {
  if (careerCache[slug]) return careerCache[slug];
  
  try {
    const response = await fetch(`/data/careers/${slug}.json`);
    if (!response.ok) return undefined;
    const data = await response.json();
    careerCache[slug] = data;
    return data;
  } catch (error) {
    console.error(`Error loading career ${slug}:`, error);
    return undefined;
  }
};

export const getCategories = (): Category[] => {
  return [...CATEGORIES].sort((a, b) => a.order - b.order);
};

export const searchCareers = (
  careers: any[],
  query: string,
  categoryFilter: string | null,
  sortMethod: 'az' | 'newest'
): any[] => {
  let result = [...careers];

  if (categoryFilter) {
    const categories = getCategories();
    const catObj = categories.find(c => c.id === categoryFilter);
    if (catObj) {
      result = result.filter(c => c.category.toLowerCase() === catObj.label.toLowerCase());
    }
  }

  if (query && query.trim()) {
    const q = query.toLowerCase().trim();
    result = result.filter(c => 
      c.title.toLowerCase().includes(q) ||
      (c.keywords || []).some((k: string) => k.toLowerCase().includes(q))
    );
  }

  result.sort((a, b) => {
    if (sortMethod === 'az') {
      return a.title.localeCompare(b.title);
    } else {
      return new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime();
    }
  });

  return result;
};
